import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  User,
  Activity,
  Calendar,
  FileText,
  ChevronRight,
  MessageSquare,
  Phone,
  MapPin,
  Search,
  Star,
  Users,
  Plus,
  Trash2,
  Video,
  ChevronLeft,
  Award,
  Filter,
  LayoutGrid,
  List as ListIcon,
  ExternalLink,
  Building2,
  ShieldCheck,
  Eye,
  Sparkles,
  Info,
  Check,
} from "lucide-react";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  useListCareTeam,
  useRemoveCareTeamMember,
  useGetProviderTypes,
  getListCareTeamQueryKey,
  useGetPendingCareTeamRequests,
  useRespondCareTeamRequest,
  API_BASE,
} from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";

const roleColors: Record<string, string> = {
  "Primary Care":
    "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200/50 dark:border-blue-700/50",
  Specialist:
    "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200/50 dark:border-purple-700/50",
  default:
    "bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border border-slate-200/50 dark:border-slate-700/50",
};

export default function CareTeam() {
  const [, setLocationNav] = useLocation();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Quick View Modal state
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);

  // GFE Location Selection Modal state
  const [gfeModalOpen, setGfeModalOpen] = useState(false);
  const [gfeProvider, setGfeProvider] = useState<any>(null);
  const [selectedGfeLocation, setSelectedGfeLocation] = useState<any>(null);

  const { data: pendingRequests = [] } = useGetPendingCareTeamRequests();
  const respondRequest = useRespondCareTeamRequest();

  const { data: teamData, isLoading } = useListCareTeam({
    page: currentPage,
    limit: itemsPerPage,
    search: search || undefined,
  });
  const rawTeam = teamData?.items || [];

  const { data: masterProviderTypes = [] } = useGetProviderTypes();

  const availableSpecialties = useMemo(() => {
    const specialtiesSet = new Set<string>();

    if (Array.isArray(masterProviderTypes) && masterProviderTypes.length > 0) {
      masterProviderTypes.forEach((pt: any) => {
        const name = typeof pt === "object" && pt !== null ? (pt.name || pt.code) : String(pt);
        if (name) specialtiesSet.add(name);
      });
    }

    rawTeam.forEach((m: any) => {
      if (m.role) specialtiesSet.add(m.role);
      if (m.specialty) specialtiesSet.add(m.specialty);
      if (m.provider?.providerTypes && Array.isArray(m.provider.providerTypes)) {
        m.provider.providerTypes.forEach((pt: any) => {
          const name = typeof pt === "object" && pt !== null ? (pt.name || pt.code) : String(pt);
          if (name) specialtiesSet.add(name);
        });
      }
    });

    if (specialtiesSet.size === 0) {
      ["Primary Care", "Specialist", "Cardiology", "Dermatology", "Pediatrics", "Internal Medicine", "Family Medicine", "Orthopedics", "Neurology"].forEach(s => specialtiesSet.add(s));
    }

    return Array.from(specialtiesSet).filter(Boolean).sort();
  }, [masterProviderTypes, rawTeam]);

  const checkIsPcpEligible = (member: any): boolean => {
    if (!member) return false;
    if (member.isPcpEligible === true) return true;

    const roleStr = (member.role || member.practiceType || "").toLowerCase();
    if (roleStr.includes("pcp") || roleStr.includes("primary care")) return true;

    const specStr = (member.specialty || member.headline || "").toLowerCase();
    if (
      specStr.includes("pcp") ||
      specStr.includes("primary care") ||
      specStr.includes("family medicine") ||
      specStr.includes("internal medicine") ||
      specStr.includes("pediatric") ||
      specStr.includes("geriatric")
    ) {
      return true;
    }

    const typesArr: any[] = member.providerTypes || member.provider?.providerTypes || [];
    if (Array.isArray(typesArr) && typesArr.length > 0) {
      return typesArr.some((pt: any) => {
        const name = (typeof pt === "string" ? pt : (pt.name || pt.code || "")).toLowerCase();
        return (
          name.includes("pcp") ||
          name.includes("primary care") ||
          name.includes("family medicine") ||
          name.includes("internal medicine") ||
          name.includes("pediatric") ||
          name.includes("geriatric")
        );
      });
    }

    return false;
  };

  const getCleanLocationStr = (locObj: any, defaultAddr: string, pName: string) => {
    let locStr = locObj?.formatted || (defaultAddr && defaultAddr !== "Address not available" ? defaultAddr : "");
    if (!locStr && (locObj?.city || locObj?.state)) {
      locStr = [locObj.street1, locObj.city, locObj.state, locObj.zipCode].filter(Boolean).join(", ");
    }
    if (!locStr || locStr === "Address not available") {
      locStr = pName ? `${pName}'s Clinic` : "Primary Clinic Location";
    }
    return locStr;
  };

  const handleInitiateGfe = (e: React.MouseEvent, provider: any) => {
    e.stopPropagation();
    const addrs = provider.addresses || [];
    const pName = provider.providerName || provider.name || "Provider";

    if (addrs.length > 1) {
      setGfeProvider(provider);
      setSelectedGfeLocation(addrs[0]);
      setGfeModalOpen(true);
    } else {
      const locObj = addrs[0] || {};
      const locStr = getCleanLocationStr(locObj, provider.address, pName);
      const stateStr = locObj.state || provider.state || "";

      setLocationNav(`/member/cost-estimates?providerId=${provider.id}&providerName=${encodeURIComponent(pName)}&location=${encodeURIComponent(locStr)}&state=${encodeURIComponent(stateStr)}`);
    }
  };

  const handleConfirmGfeLocation = () => {
    if (!gfeProvider) return;
    const pName = gfeProvider.providerName || gfeProvider.name || "Provider";
    const locStr = getCleanLocationStr(selectedGfeLocation, gfeProvider.address, pName);
    const stateStr = selectedGfeLocation?.state || gfeProvider.state || "";

    setGfeModalOpen(false);
    setLocationNav(`/member/cost-estimates?providerId=${gfeProvider.id}&providerName=${encodeURIComponent(pName)}&location=${encodeURIComponent(locStr)}&state=${encodeURIComponent(stateStr)}`);
  };

  const filteredTeam = useMemo(() => {
    if (selectedCategory === "ALL") return rawTeam;
    return rawTeam.filter((m: any) => {
      const r = (m.role || "").toLowerCase();
      const s = (m.specialty || "").toLowerCase();
      const target = selectedCategory.toLowerCase();

      let matchesType = false;
      if (m.provider?.providerTypes && Array.isArray(m.provider.providerTypes)) {
        matchesType = m.provider.providerTypes.some((pt: any) => {
          const name = (typeof pt === "object" && pt !== null ? (pt.name || pt.code) : String(pt)).toLowerCase();
          return name.includes(target);
        });
      }

      return r.includes(target) || s.includes(target) || matchesType;
    });
  }, [rawTeam, selectedCategory]);

  const totalEntries = filteredTeam.length;
  const totalPages = Math.ceil(totalEntries / itemsPerPage) || 1;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const remove = useRemoveCareTeamMember();

  const [pcpProviderId, setPcpProviderId] = useState<string>(() => {
    try {
      const u = localStorage.getItem("user");
      const userId = u ? JSON.parse(u)?.id : "";
      return localStorage.getItem(`pcp_provider_${userId}`) || "";
    } catch {
      return "";
    }
  });

  const handleTogglePcp = (
    e: React.MouseEvent,
    provider: any
  ) => {
    e.stopPropagation();
    const providerId = provider.id || provider.providerId;
    const providerName = provider.providerName || provider.name || "Provider";

    const isEligible = checkIsPcpEligible(provider);

    if (pcpProviderId === providerId) {
      // Remove PCP
      try {
        const u = localStorage.getItem("user");
        const userId = u ? JSON.parse(u)?.id : "";
        setPcpProviderId("");
        localStorage.removeItem(`pcp_provider_${userId}`);
        toast({
          title: "Primary Care Provider Removed",
          description: `${providerName} is no longer set as your Primary Care Provider.`,
        });
      } catch (err) {
        console.error(err);
      }
    } else {
      // Validate PCP eligibility
      if (!isEligible) {
        toast({
          variant: "destructive",
          title: "PCP Designation Restricted",
          description: `${providerName} is registered as a specialist (${provider.specialty || 'Specialist'}) and has not listed Primary Care (PCP) in their provider specialty. Only providers with Primary Care credentials can be set as PCP.`,
        });
        return;
      }

      try {
        const u = localStorage.getItem("user");
        const userId = u ? JSON.parse(u)?.id : "";
        setPcpProviderId(providerId);
        localStorage.setItem(`pcp_provider_${userId}`, providerId);
        toast({
          title: "Primary Care Provider Updated",
          description: `${providerName} is now set as your Primary Care Provider (PCP).`,
        });
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleRemove = (id: string, name: string) => {
    remove.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCareTeamQueryKey() });
          toast({
            title: "Removed from care team",
            description: `${name} has been removed.`,
          });
          if (selectedProvider?.id === id) {
            setQuickViewOpen(false);
          }
        },
      }
    );
  };

  const handleOpenQuickView = (provider: any) => {
    setSelectedProvider(provider);
    setQuickViewOpen(true);
  };

  const getProviderSlug = (name: string) => {
    return (name || 'provider')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const startIndex =
    totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalEntries);
  const paginatedTeam = filteredTeam;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-7 pt-4 sm:pt-6 lg:pt-8 pb-16 flex flex-col min-h-[calc(100vh-10rem)]">
        <div className="space-y-6 flex-1">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-3.5 sm:gap-4">
              <div className="p-3 sm:p-3.5 bg-primary/10 rounded-2xl flex-shrink-0 text-primary">
                <Users className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <div className="space-y-0.5">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  Care Team
                </h1>
                <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium">
                  Your trusted healthcare providers
                </p>
              </div>
            </div>
            <Link href="/member/search">
              <Button
                className="w-full sm:w-auto bg-primary text-white hover:bg-primary/90 border-0 rounded-xl gap-2 shadow-sm shadow-primary/25 h-11 px-6 text-xs sm:text-sm font-bold cursor-pointer transition-all hover:shadow-md active:scale-[0.99]"
                data-testid="button-add-provider"
              >
                <Plus className="w-4 h-4 shrink-0" /> Add Provider
              </Button>
            </Link>
          </div>

          {/* Pending Connection Requests Section */}
          {pendingRequests && pendingRequests.length > 0 && (
            <div className="p-5 sm:p-6 rounded-2xl sm:rounded-3xl bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-200 space-y-4 shadow-xs">
              <div className="flex items-center gap-2.5 font-bold text-sm sm:text-base">
                <Users className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                <span>Pending Care Team Connection Requests ({pendingRequests.length})</span>
              </div>
              <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-300 font-medium">
                The following healthcare provider(s) have requested to link with your account and be added to your care team.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                {pendingRequests.map((req: any) => {
                  const providerName = req.provider?.user?.displayName || `${req.provider?.user?.firstName || ''} ${req.provider?.user?.lastName || ''}`.trim() || 'Healthcare Provider';
                  const groupName = req.provider?.practiceGroups?.[0]?.practiceGroup?.name;

                  return (
                    <div key={req.id} className="p-4 rounded-2xl bg-card border border-amber-500/20 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3.5">
                      <div className="space-y-1 min-w-0 flex-1">
                        <p className="font-bold text-sm text-foreground truncate">{providerName}</p>
                        {groupName && <p className="text-xs text-muted-foreground truncate">{groupName}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-xl text-xs font-semibold border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 cursor-pointer"
                          disabled={respondRequest.isPending}
                          onClick={() => {
                            respondRequest.mutate({ id: req.id, action: 'REJECT' }, {
                              onSuccess: () => {
                                toast({ title: "Request Declined", description: "Care team request declined." });
                                queryClient.invalidateQueries({ queryKey: ['getPendingCareTeamRequests'] });
                                queryClient.invalidateQueries({ queryKey: getListCareTeamQueryKey() });
                              }
                            });
                          }}
                        >
                          Decline
                        </Button>
                        <Button
                          size="sm"
                          className="h-8 rounded-xl text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold cursor-pointer"
                          disabled={respondRequest.isPending}
                          onClick={() => {
                            respondRequest.mutate({ id: req.id, action: 'ACCEPT' }, {
                              onSuccess: () => {
                                toast({ title: "Request Approved", description: `${providerName} added to your care team!` });
                                queryClient.invalidateQueries({ queryKey: ['getPendingCareTeamRequests'] });
                                queryClient.invalidateQueries({ queryKey: getListCareTeamQueryKey() });
                              }
                            });
                          }}
                        >
                          Approve
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search Bar, Specialty Filter & View Mode Controls */}
          {(rawTeam?.length ?? 0) > 0 && (
            <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3.5 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 min-w-0">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search your care team..."
                    className="pl-11 h-11 text-xs sm:text-sm rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 focus-visible:ring-primary/30 shadow-none"
                  />
                </div>

                <div className="w-full sm:w-56 shrink-0">
                  <Select
                    value={selectedCategory}
                    onValueChange={(val) => {
                      setSelectedCategory(val);
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="h-11 text-xs sm:text-sm rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 shadow-none font-medium cursor-pointer">
                      <SelectValue placeholder="All Specialties" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl shadow-xl z-50">
                      <SelectItem value="ALL">All Specialties</SelectItem>
                      {availableSpecialties.map((spec) => (
                        <SelectItem key={spec} value={spec}>
                          {spec}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* View Mode Toggle: Grid vs List */}
              <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800/80 p-1 rounded-2xl border border-slate-200 dark:border-slate-700/60 self-start sm:self-end lg:self-auto shrink-0">
                <Button
                  type="button"
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className={`h-9 px-3.5 rounded-xl text-xs font-bold gap-1.5 transition-all cursor-pointer ${
                    viewMode === "grid" ? "bg-primary text-primary-foreground shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                  data-testid="view-mode-grid"
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span>Column View</span>
                </Button>
                <Button
                  type="button"
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className={`h-9 px-3.5 rounded-xl text-xs font-bold gap-1.5 transition-all cursor-pointer ${
                    viewMode === "list" ? "bg-primary text-primary-foreground shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                  data-testid="view-mode-list"
                >
                  <ListIcon className="w-4 h-4" />
                  <span>List View</span>
                </Button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-slate-900 rounded-3xl p-5 shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <Skeleton className="w-14 h-14 rounded-2xl flex-shrink-0" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-36 mb-2" />
                      <Skeleton className="h-4 w-24 mb-2" />
                      <Skeleton className="h-6 w-20 rounded-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (rawTeam?.length ?? 0) === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-slate-900 rounded-3xl p-12 text-center shadow-sm border border-slate-100 dark:border-slate-800"
            >
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-slate-300 dark:text-slate-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-2">
                No providers yet
              </h2>
              <p className="text-slate-400 text-sm mb-6 max-w-xs mx-auto">
                Add your primary care provider and specialists to keep them all in
                one place.
              </p>
              <Link href="/member/search">
                <Button
                  className="bg-primary text-white border-0 rounded-full shadow-sm shadow-primary/30 h-10 px-6"
                  data-testid="button-find-providers"
                >
                  Find a Provider
                </Button>
              </Link>
            </motion.div>
          ) : viewMode === "list" ? (
            /* List View Layout */
            <div className="bg-card rounded-3xl border border-border shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/60 text-muted-foreground font-semibold uppercase tracking-wider text-[11px]">
                      <th className="py-3.5 px-5">Provider Name</th>
                      <th className="py-3.5 px-5">Type 1 NPI</th>
                      <th className="py-3.5 px-5 hidden sm:table-cell">Email</th>
                      <th className="py-3.5 px-5 hidden md:table-cell">Location</th>
                      <th className="py-3.5 px-5 text-right">GFE</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {paginatedTeam.map((member) => (
                      <tr
                        key={member.id}
                        onClick={() => handleOpenQuickView(member)}
                        className="hover:bg-accent/60 transition-colors cursor-pointer group"
                        data-testid={`care-team-row-${member.id}`}
                      >
                        {/* Provider Name */}
                        <td className="py-3.5 px-5">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-9 h-9 border border-primary/20 flex-shrink-0 group-hover:scale-105 transition-transform">
                              <AvatarFallback className="bg-gradient-to-br from-primary to-indigo-600 text-white font-extrabold text-sm">
                                {member.providerName?.charAt(0) || "P"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-bold text-sm text-foreground group-hover:text-primary transition-colors truncate max-w-[180px]">
                                {member.providerName}
                              </p>
                              {member.credentials && (
                                <p className="text-[11px] text-muted-foreground font-medium truncate">
                                  {member.credentials}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Type 1 NPI */}
                        <td className="py-3.5 px-5">
                          {member.npiNumber ? (
                            <span className="font-mono text-xs font-bold text-foreground bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg tracking-wide border border-slate-200 dark:border-slate-700">
                              {member.npiNumber}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Email */}
                        <td className="py-3.5 px-5 hidden sm:table-cell">
                          {member.email ? (
                            <a
                              href={`mailto:${member.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary hover:underline text-xs font-medium truncate max-w-[200px] block"
                              title={member.email}
                            >
                              {member.email}
                            </a>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Location */}
                        <td className="py-3.5 px-5 hidden md:table-cell">
                          <div className="flex items-start gap-1.5 max-w-[220px]">
                            <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-muted-foreground truncate leading-snug">
                              {(member.address && member.address !== "Address not available")
                                ? member.address
                                : "Address not on file"}
                            </span>
                          </div>
                        </td>

                        {/* GFE */}
                        <td className="py-3.5 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            onClick={(e) => handleInitiateGfe(e, member)}
                            className="h-8 px-3 text-xs font-bold rounded-xl gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs whitespace-nowrap"
                            data-testid={`gfe-btn-${member.id}`}
                          >
                            <Sparkles className="w-3.5 h-3.5" /> Generate GFE
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          ) : (
            /* Column / Grid View Layout */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
              {paginatedTeam.map((member, i) => {
                const isPcp = pcpProviderId === member.id;
                const isEligible = checkIsPcpEligible(member);

                return (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: i * 0.08,
                      type: "spring",
                      stiffness: 100,
                    }}
                    onClick={() => handleOpenQuickView(member)}
                    className={`group relative bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-xs border transition-all duration-300 overflow-hidden cursor-pointer flex flex-col justify-between hover:shadow-md ${
                      isPcp
                        ? "border-blue-400/80 ring-2 ring-blue-500/20 shadow-blue-500/10 bg-gradient-to-b from-blue-50/20 via-white to-white dark:from-blue-950/20 dark:via-slate-900 dark:to-slate-900"
                        : "border-slate-200/80 dark:border-slate-800 hover:border-primary/30"
                    }`}
                    data-testid={`care-team-member-${member.id}`}
                  >
                    {/* Background visual element */}
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-28 h-28 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                    <div>
                      {/* Top Header Row: Badges & Remove Button */}
                      <div className="flex items-center justify-between gap-2 mb-4 relative z-10">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                          <span
                            className={`text-[11px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1 rounded-full ${
                              roleColors[member.role] ?? roleColors.default
                            }`}
                          >
                            {member.role}
                          </span>

                          {member.status === "active" && (
                            <span className="text-[11px] sm:text-xs bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20 rounded-full px-2.5 sm:px-3 py-1 font-semibold flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                              Active
                            </span>
                          )}

                          {isPcp && (
                            <span className="px-2.5 sm:px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] sm:text-[11px] font-bold flex items-center gap-1 shadow-xs shrink-0">
                              <Award className="w-3.5 h-3.5 fill-current shrink-0" />
                              PCP Provider
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove(member.id, member.providerName);
                          }}
                          className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors flex-shrink-0 cursor-pointer"
                          title="Remove member"
                          data-testid={`button-remove-member-${member.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Main Provider Profile Details */}
                      <div className="flex items-start gap-3.5 sm:gap-4 relative z-10">
                        {/* Avatar */}
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-primary via-indigo-600 to-primary/80 shadow-sm shadow-primary/20 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                          <span className="text-xl sm:text-2xl font-bold text-white">
                            {member.providerName.charAt(0)}
                          </span>
                        </div>

                        {/* Info Block */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div>
                            <p className="font-bold text-base sm:text-lg text-slate-900 dark:text-white group-hover:text-primary transition-colors truncate">
                              {member.providerName}
                            </p>
                            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                              {member.specialty}
                            </p>
                          </div>

                          {/* Rating & Creds */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <div className="flex items-center gap-1 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-lg border border-amber-500/20">
                              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />
                              <span className="text-xs font-bold">
                                {member.rating}
                              </span>
                            </div>
                            <span className="text-xs text-slate-400 font-medium">
                              ({member.reviewCount} reviews)
                            </span>
                            {member.credentials && (
                              <>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate max-w-[200px]">
                                  {member.credentials}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Address */}
                          <div className="flex items-start gap-1.5 pt-0.5">
                            <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2">
                              {member.address}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dedicated Actions Strip */}
                    <div className="mt-5 pt-3.5 sm:pt-4 border-t border-slate-100 dark:border-slate-800/80 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 relative z-10">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenQuickView(member);
                        }}
                        className="w-full sm:w-auto h-9 px-3.5 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-primary/10 hover:text-primary dark:hover:bg-primary/20 transition-colors flex items-center justify-center gap-1.5 cursor-pointer order-last sm:order-first"
                      >
                        <Eye className="w-3.5 h-3.5 shrink-0" /> Quick View
                      </button>

                      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => handleInitiateGfe(e, member)}
                          className="flex-1 sm:flex-initial h-9 px-3.5 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 shadow-xs cursor-pointer whitespace-nowrap"
                        >
                          <Sparkles className="w-3.5 h-3.5 shrink-0" /> Generate GFE
                        </button>

                        {(isPcp || isEligible) && (
                          <button
                            type="button"
                            onClick={(e) => handleTogglePcp(e, member)}
                            className={`flex-1 sm:flex-initial h-9 px-3 text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-xs group/pcp cursor-pointer whitespace-nowrap ${
                              isPcp
                                ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-blue-500/25 ring-2 ring-blue-400/40"
                                : "bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-600 border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                            }`}
                            title={isPcp ? "Click to remove as Primary Care Provider" : "Click to set as Primary Care Provider"}
                          >
                            <Award className={`w-3.5 h-3.5 shrink-0 ${isPcp ? "fill-current" : ""}`} />
                            {isPcp ? (
                              <>
                                <span className="group-hover/pcp:hidden">PCP Provider</span>
                                <span className="hidden group-hover/pcp:inline">Remove PCP</span>
                              </>
                            ) : (
                              "Set as PCP"
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Empty state for search */}
              {!isLoading && totalEntries === 0 && search && (
                <div className="col-span-full py-12 text-center text-slate-400">
                  <Search className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="font-semibold text-slate-600 dark:text-slate-400 mb-1">
                    No team members match your search
                  </p>
                  <p className="text-sm">
                    Try using different keywords or clearing your search.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearch("");
                      setCurrentPage(1);
                    }}
                    className="mt-4 rounded-full"
                  >
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && totalEntries > 0 && (
          <div className="mt-auto flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100 dark:border-slate-800 shrink-0 text-[13px] text-slate-600 dark:text-slate-400">
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-slate-200 dark:border-slate-700 rounded-md bg-transparent px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer text-xs"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <span className="text-xs">
                Showing {startIndex} to {endIndex} of {totalEntries} entries
              </span>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-md border border-slate-200 dark:border-slate-700 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {getPageNumbers().map((p, index) => (
                <button
                  key={index}
                  onClick={() => typeof p === "number" && setCurrentPage(p)}
                  disabled={p === "..."}
                  className={`min-w-[32px] h-8 rounded-md flex items-center justify-center font-medium transition-colors cursor-pointer ${
                    p === currentPage
                      ? "bg-blue-600 text-white border border-blue-600"
                      : p === "..."
                      ? "cursor-default text-slate-400"
                      : "hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700"
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage >= totalPages}
                className="p-1.5 rounded-md border border-slate-200 dark:border-slate-700 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* GFE Location Selector Modal */}
      <Dialog open={gfeModalOpen} onOpenChange={setGfeModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full max-w-[540px] max-h-[88vh] sm:max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-5 sm:p-6 bg-card border border-border shadow-2xl space-y-5">
          <DialogHeader className="pr-8 sm:pr-9 text-left space-y-1">
            <DialogTitle className="text-xl font-extrabold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600 shrink-0" />
              Generate Good Faith Estimate
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Select which clinic location for <strong className="text-foreground">{gfeProvider?.providerName}</strong> you wish to generate the estimate for.
            </DialogDescription>
          </DialogHeader>

          {gfeProvider && (
            <div className="space-y-4">
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {(gfeProvider.addresses || []).map((addr: any, idx: number) => {
                  const isSelected = (selectedGfeLocation?.id || selectedGfeLocation?.formatted) === (addr.id || addr.formatted);

                  return (
                    <div
                      key={addr.id || idx}
                      onClick={() => setSelectedGfeLocation(addr)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-start justify-between gap-3 ${
                        isSelected
                          ? "border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/20"
                          : "border-border bg-muted/40 hover:bg-muted/70"
                      }`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <h4 className="font-bold text-xs text-foreground">
                            {addr.street1 || addr.city ? `${addr.street1 || ''}, ${addr.city || ''}` : `Location ${idx + 1}`}
                          </h4>
                          {addr.isPrimary && (
                            <Badge className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                              Primary
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground pl-6">
                          {addr.formatted || [addr.street1, addr.street2, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ')}
                        </p>
                      </div>

                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? "border-emerald-600 bg-emerald-600 text-white" : "border-muted-foreground/30"
                      }`}>
                        {isSelected && <Check className="w-3.5 h-3.5" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setGfeModalOpen(false)}
                  className="rounded-xl text-xs font-bold h-10 px-4 cursor-pointer"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirmGfeLocation}
                  className="rounded-xl text-xs font-bold h-10 px-5 bg-emerald-600 text-white hover:bg-emerald-700 shadow-md gap-1.5 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 shrink-0" /> Proceed to GFE
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Provider Quick View Popup / Modal */}
      <Dialog open={quickViewOpen} onOpenChange={setQuickViewOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full max-w-[620px] max-h-[88vh] sm:max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-4 sm:p-6 bg-card border border-border shadow-2xl">
          {selectedProvider && (() => {
            const isPcp = pcpProviderId === selectedProvider.id;
            const isEligible = checkIsPcpEligible(selectedProvider);

            return (
              <div className="space-y-5 sm:space-y-6">
                {/* Quick View Header */}
                <div className="flex items-start gap-3 sm:gap-4 pb-4 border-b border-border pr-8 sm:pr-9">
                  <Avatar className="w-12 h-12 sm:w-16 sm:h-16 border-2 border-primary/20 shadow-md flex-shrink-0">
                    <AvatarFallback className="bg-gradient-to-br from-primary to-indigo-600 text-white text-base sm:text-xl font-extrabold">
                      {selectedProvider.providerName?.charAt(0) || "P"}
                    </AvatarFallback>
                  </Avatar>

                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg sm:text-xl font-extrabold text-foreground truncate">
                        {selectedProvider.providerName}
                      </h3>
                      {isPcp && (
                        <Badge className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 font-bold text-[10px] sm:text-xs gap-1 py-0.5 px-2.5 rounded-full shrink-0">
                          <Award className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current" /> Primary Care Provider (PCP)
                        </Badge>
                      )}
                    </div>

                    <p className="text-xs font-semibold text-primary truncate">
                      {selectedProvider.specialty} {selectedProvider.credentials ? `• ${selectedProvider.credentials}` : ''}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground pt-0.5">
                      <div className="flex items-center gap-1 font-bold text-amber-500">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0" />
                        <span>{selectedProvider.rating || '5.0'}</span>
                      </div>
                      <span>•</span>
                      <span>{selectedProvider.reviewCount || '0'} patient reviews</span>
                    </div>
                  </div>
                </div>

                {/* Provider Quick Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 text-xs">
                  <div className="p-3 sm:p-3.5 rounded-2xl bg-muted/40 border border-border space-y-1">
                    <span className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-primary shrink-0" /> Practice & Role
                    </span>
                    <p className="font-extrabold text-foreground truncate text-xs sm:text-sm">
                      {selectedProvider.role || 'Healthcare Provider'}
                    </p>
                  </div>

                  <div className="p-3 sm:p-3.5 rounded-2xl bg-muted/40 border border-border space-y-1">
                    <span className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" /> Care Status
                    </span>
                    <p className="font-extrabold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 text-xs sm:text-sm">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                      Active Care Team Provider
                    </p>
                  </div>

                  <div className="sm:col-span-2 p-3 sm:p-3.5 rounded-2xl bg-muted/40 border border-border space-y-1">
                    <span className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-primary shrink-0" /> Primary Clinic Location
                    </span>
                    <p className="font-medium text-foreground text-xs sm:text-sm leading-relaxed">
                      {selectedProvider.address || 'Location information available on full profile page.'}
                    </p>
                  </div>

                  {!isEligible && (
                    <div className="sm:col-span-2 p-3 sm:p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] leading-relaxed">
                        This provider is registered as a specialist and has not designated Primary Care (PCP) in their provider specialty.
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions Strip */}
                <div className="pt-3 sm:pt-4 border-t border-border flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      onClick={(e) => {
                        setQuickViewOpen(false);
                        handleInitiateGfe(e, selectedProvider);
                      }}
                      className="w-full sm:w-auto h-10 px-3.5 rounded-xl text-xs font-bold gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm cursor-pointer col-span-2 sm:col-span-1"
                    >
                      <Sparkles className="w-4 h-4 shrink-0" /> Generate GFE
                    </Button>

                    {(isPcp || isEligible) && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => handleTogglePcp(e, selectedProvider)}
                        className="w-full sm:w-auto h-10 px-3.5 rounded-xl text-xs font-bold gap-1.5 border-border cursor-pointer"
                      >
                        <Award className="w-4 h-4 text-blue-600 shrink-0" />
                        {isPcp ? "Remove as PCP" : "Set as PCP"}
                      </Button>
                    )}

                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleRemove(selectedProvider.id, selectedProvider.providerName)}
                      className="w-full sm:w-auto h-10 px-3 rounded-xl text-xs font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 gap-1.5 cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4 shrink-0" /> Remove
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
                    <Link href={`/member/appointments?providerId=${selectedProvider.id}&providerName=${encodeURIComponent(selectedProvider.providerName)}`} className="w-full sm:w-auto">
                      <Button type="button" variant="outline" className="w-full sm:w-auto h-10 px-4 rounded-xl text-xs font-bold gap-1.5 border-primary/30 text-primary hover:bg-primary/10 cursor-pointer">
                        <Calendar className="w-4 h-4 shrink-0" /> Book Visit
                      </Button>
                    </Link>

                    <a
                      href={`/provider/${getProviderSlug(selectedProvider.providerName)}/${selectedProvider.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto"
                    >
                      <Button type="button" className="w-full sm:w-auto h-10 px-4 rounded-xl text-xs font-bold gap-1.5 bg-primary text-white hover:bg-primary/90 shadow-sm cursor-pointer">
                        Full Provider Page <ExternalLink className="w-4 h-4 shrink-0" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </MemberLayout>
  );
}
